import { BrandfolderHexColors } from '@brandfolder/react';
import classNames from 'classnames';
import { forwardRef, HTMLAttributes, ReactNode, useContext } from 'react';
import FocusLock, { AutoFocusInside } from 'react-focus-lock';
import styled from 'styled-components';

import { DragAndDropContext } from '@context/drag-and-drop';
import { screenReaderOnly } from '@styles/screen-reader';

interface AsideDialogProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  id: string;
  title: string;
}

export type OmittedAsideDialogProps = Omit<AsideDialogProps, 'children' | 'id' | 'title'>;

const UnstyledAsideDialog = forwardRef<HTMLDivElement, AsideDialogProps>((props, ref) => {
  const { children, id, title, ...otherProps } = props;

  const headingId = `${id}-heading`;

  const { dragged } = useContext(DragAndDropContext);

  return (
    <div
      {...props}
      aria-labelledby={headingId}
      aria-modal="true"
      className={classNames({
        'aside-dialog': true,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      id={id}
      ref={ref}
      role="dialog"
      style={{
        display: dragged ? 'none' : undefined
      }}
    >
      <div className="aside-dialog__inner">
        <h2 className="aside-dialog__heading" id={headingId}>
          {title}
        </h2>
        <FocusLock>
          <AutoFocusInside>{children}</AutoFocusInside>
        </FocusLock>
      </div>
    </div>
  );
});

const StyledAsideDialog = styled(UnstyledAsideDialog)`
  background-color: ${BrandfolderHexColors.Text};
  box-shadow: 0px 4px 10px 1px rgb(0 0 0 / 12%);
  height: calc(100vh - 78px);
  left: 65px;
  position: absolute;
  top: 0;
  width: 65px;

  .has-scrolled & {
    height: 100vh;
  }

  .aside-dialog__inner {
    padding: 10px;
  }

  .aside-dialog__heading {
    ${screenReaderOnly}
  }
`;

export const AsideDialog = forwardRef<HTMLDivElement, AsideDialogProps>((props, ref) => {
  return <StyledAsideDialog {...props} ref={ref} />;
});
